import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

export const useAxiosAuthenticated = () => {
    const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
    const tokenRef = useRef('');
    const [tokenError, _setTokenError] = useState();

    useEffect(() => {
        if (!isAuthenticated || isLoading) return;

        let isMounted = true;
        const getToken = async () => {
            try {
                const token = await getAccessTokenSilently();
                if (!isMounted) return;
                tokenRef.current = token;
                _setTokenError(undefined);
            } catch (error) {
                if (!isMounted) return;
                _setTokenError(error);
            }
        };

        getToken();

        return () => {
            isMounted = false;
        };
    }, [getAccessTokenSilently, isAuthenticated, isLoading]);

    const waitForToken = useCallback(async () => {
        while (!tokenRef.current && !tokenError) {
            await new Promise((resolve) => setTimeout(resolve, 250));
        }

        if (tokenError) {
            throw tokenError;
        }

        return tokenRef.current;
    }, [tokenError]);

    const get = useCallback(
        async (url) => {
            const token = await waitForToken();

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            };

            return axios.get(url, config);
        },
        [waitForToken]
    );

    const post = useCallback(
        async (url, body) => {
            const token = await waitForToken();

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            };

            return axios.post(url, body, config);
        },
        [waitForToken]
    );

    return { get, post, tokenError };
};
